import * as React from 'react';
import { useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { navigate } from 'wouter/use-location';
import PermissionsService, { PERMISSION_BLUECOINS } from '../../../../Services/PermissionsService';
import LoaderService from '../../../../Services/LoaderService';
import { activate, datepickerSave, deactivate, isValidDate } from '../../BluecoinsService';
import { BluecoinRecord } from '../BluecoinsListState';
import {
  BLE_TABLE_NAME,
  BLE_USAGE_TABLE_NAME,
  IS_ACTIVE,
  IS_DISCARDED,
  IS_INACTIVE,
  IS_OK,
  STATE_KEY,
  UUID_KEY,
} from '../../constants';
import { ICmdTableFilter, IFilterChipsValues } from '../../../CmdTable/types';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../../../store/recoil/TableState';
import { TIMESTAMP_KEY } from '../../../TripsOverviewNew/constants';
import { selectedCustomersAtom } from '../../../../store/recoil/customers';
import {
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../../../CmdTable/CmdTableState';
import useAnalytics from '../../../../hooks/useAnalytics';
import { getTableName } from '../../../CmdTable/CmdTableService';

interface IActionMenuProps<T extends BluecoinRecord> {
  data: T;
  showDetailModalCallback: React.Dispatch<React.SetStateAction<boolean>>;
  showDiscardConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  showUseCreationSettingsModalCallback: React.Dispatch<React.SetStateAction<boolean>>;
  closeCallback: () => void;
  position: { top: number; left: number };
}

const ActionsMenu = React.forwardRef<HTMLDivElement, IActionMenuProps<BluecoinRecord>>(
  (props, ref) => {
    const { formatMessage: f } = useIntl();
    const queryClient = useQueryClient();
    const filterForRequest = useRecoilValue<ICmdTableFilter>(
      filterForRequestAtomFamily(BLE_TABLE_NAME)
    );
    const sortFilter = useRecoilValue<{ [key: string]: string }>(
      sortFilterAtomFamily(BLE_TABLE_NAME)
    );
    const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
    const setFilterForRequestBleUsage = useSetRecoilState<ICmdTableFilter>(
      filterForRequestAtomFamily(BLE_USAGE_TABLE_NAME)
    );
    const setTableFiltersKeys = useSetRecoilState<string[]>(
      tableFiltersAtomFamily(BLE_USAGE_TABLE_NAME)
    );
    const setTableActionsFilters = useSetRecoilState(
      tableFilterAtomFamily({ [BLE_USAGE_TABLE_NAME]: UUID_KEY })
    );
    const [datepickerSettings, setDatepickerSettings] = useRecoilState<IDatepickerSettings | null>(
      setTableDatepickerAtom
    );
    const { trackEvent } = useAnalytics();

    const updateCellValue = (id: number, key: string, value: string) => {
      queryClient.setQueryData(
        [BLE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
        (oldData: any) => {
          if (!oldData) return oldData;
          const newData = {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              records: page.records.map((record: BluecoinRecord) =>
                record.id === id ? { ...record, [key]: value } : record
              ),
            })),
          };
          return newData;
        }
      );
    };

    const handleActivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      props.closeCallback();
      LoaderService.showLoader();
      const data = await activate([props.data.id]);
      LoaderService.showLoader(false);
      if (data && data.status === IS_OK) {
        updateCellValue(props.data.id, STATE_KEY, IS_ACTIVE);
      }
    };

    const handleDeactivate = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      props.closeCallback();
      LoaderService.showLoader();
      const data = await deactivate([props.data.id]);
      LoaderService.showLoader(false);
      if (data && data.status === IS_OK) {
        updateCellValue(props.data.id, STATE_KEY, IS_INACTIVE);
      }
    };

    const handleDetail = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      props.showDetailModalCallback(true);
    };

    const handleUseCreationSettings = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      props.showUseCreationSettingsModalCallback(true);
    };

    const handleUsageFilter = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      const timeStart = isValidDate(String(props.data.usageStart.timestamp));
      const timeEnd = isValidDate(String(props.data.lastUsage.timestamp));
      navigate(`/bluecoins/usage?uuid=${props.data.uuid}`);
      trackEvent([`table ${getTableName(BLE_TABLE_NAME)}`, 'click', 'Open Usage tab']);

      setTableFiltersKeys((prevFilter) => [...prevFilter, UUID_KEY]);
      if (timeStart && timeEnd) {
        const response = await datepickerSave(
          [props.data.usageStart.timestamp, props.data.lastUsage.timestamp],
          null
        );
        if (response) {
          const type = 'date_range_picker';
          setDatepickerSettings({
            ...datepickerSettings,
            [TIMESTAMP_KEY]: {
              type,
              date: [props.data.usageStart.timestamp, props.data.lastUsage.timestamp],
              interval: null,
            },
          });
        }
        setFilterForRequestBleUsage((prevFilter) => ({
          ...prevFilter,
          uuid: props.data.uuid,
          timestamp: {
            min: String(props.data.usageStart.timestamp),
            max: String(props.data.lastUsage.timestamp),
          },
        }));
      } else {
        setFilterForRequestBleUsage((prevFilter) => ({
          ...prevFilter,
          uuid: props.data.uuid,
          timestamp: {
            min: String(datepickerSettings?.timestamp.date[0]),
            max: String(datepickerSettings?.timestamp.date[1]),
          },
        }));
      }

      const newValues: IFilterChipsValues = {
        item: props.data.uuid,
        value: props.data.uuid,
        title: f({ id: `table_list.columns.bluecoinUsage.${UUID_KEY}` }),
        type: 'inputSearch',
      };
      setTableActionsFilters((tableActionFilter) => ({
        ...tableActionFilter,
        id: UUID_KEY,
        values: { ...newValues },
        isOpen: false,
        isActionChipsOpen: false,
        isFilterActive: true,
        data: { required: false, type: '', value: '', values: [] },
      }));
    };

    const menu = (
      <div
        className="ridebook-dropdown-menu dropdown-menu dropdown-menu-right dropdown-menu-custom fs-2"
        style={{
          display: 'block',
          width: '160px',
          position: 'absolute',
          top: props.position.top,
          left: props.position.left,
          zIndex: 1,
        }}
        aria-labelledby="dropdown-actions-1"
        ref={ref}
      >
        {props.data && (
          <div>
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read']) && (
              <a
                data-cy="action-detail"
                href="#"
                title={f({ id: 'table_list.actions.detail' })}
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                onClick={handleDetail}
                id={`action-detail-${props.data.id}`}
              >
                <i className="cmd-icons cmd-icon-detail mr-2 fs-4" />
                {f({ id: 'table_list.actions.detail' })}
              </a>
            )}
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
              props.data.state === IS_INACTIVE && (
                <a
                  data-cy="action-activate"
                  href="#"
                  title={f({ id: 'table_list.actions.activate' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-activate-${props.data.id}`}
                  onClick={handleActivate}
                >
                  <i className="cmd-icons cmd-icon-active mr-2 fs-4" />
                  {f({ id: 'table_list.actions.activate' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
              props.data.state === IS_ACTIVE && (
                <a
                  data-cy="action-deactivate"
                  href="#"
                  title={f({ id: 'table_list.actions.deactivate' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-deactivate-${props.data.id}`}
                  onClick={handleDeactivate}
                >
                  <i className="cmd-icons cmd-icon-inactive mr-2 fs-4" />
                  {f({ id: 'table_list.actions.deactivate' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
              props.data.state !== IS_DISCARDED && (
                <a
                  data-cy="action-discard"
                  href="#"
                  title={f({ id: 'table_list.actions.discard' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-discard-${props.data.id}`}
                  onClick={props.showDiscardConfirmModalCallback}
                >
                  <i className="cmd-icons cmd-icon-bin mr-2 fs-4" />{' '}
                  {f({ id: 'table_list.actions.discard' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read']) &&
              props.data.state !== IS_DISCARDED && (
                <a
                  data-cy="action-use-creation-settings"
                  href="#"
                  title={f({ id: 'table_list.actions.use_creation_settings' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-use-creation-settings-${props.data.id}`}
                  onClick={handleUseCreationSettings}
                >
                  <i className="cmd-icons cmd-icon-bluecoin-set-up mr-2 fs-4" />{' '}
                  {f({ id: 'table_list.actions.use_creation_settings' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read']) && (
              // <Link href={`/bluecoins/usage?uuid=${props.data.uuid}`}>
              <a
                href="#"
                data-cy="action-usage"
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                id={`action-usage-${props.data.id}`}
                onClick={handleUsageFilter}
              >
                <i className="cmd-icons cmd-icon-listing mr-2 fs-4" />{' '}
                {f({ id: 'table_list.tabs.bluecoin.usage' })}
              </a>
              // </Link>
            )}
          </div>
        )}
      </div>
    );

    return ReactDOM.createPortal(menu, document.body);
  }
);

export default ActionsMenu;
